import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { translate } from './Translate';
import Icon from './Icon';
import Badge from './Badge';
import { IdentifierSearch } from '../models/configuration/IdentifierSearch';
import { AccessibilityStoreSearchKey } from '../models/configuration/accessibility/search';
import { AccessibilityStore, LoadingStore, TranslationStore } from '../stores';
import { computed } from 'mobx';
import VisuallyHiddenWrapper from './VisuallyHiddenWrapper';
import { KEYBOARD_KEYS } from '../constants';
import { Theme } from '../models/Theme';

const SEARCH_INPUT_ID = 'shareClassSearch';

const SearchWrapper = styled<any, any>('div', 'searchWrapper', 'search')`
    ${propStyles};
`;
Badge.rightAlignStyleProps = {
    order: 1,
};
Icon.rightAlignStyleProps = {
    order: 2,
};
SearchWrapper.defaultProps = {
    flex: '1 0 auto',
    display: 'flex',
    order: ['1', '0'],
    alignItems: 'center',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    paddingX: 'sm',
    paddingY: 'xs',
    minHeight: '40px',
};
const SearchClearButton = styled<any, any>(
    'button',
    'searchClearButton',
    'search'
)`
    all: unset;
    ${propStyles};
`;

const SearchInput = styled<any, any>('input', 'searchInput', 'search')`
    ${propStyles};
    &::-ms-clear {
        display: none;
    }
    &::-webkit-search-cancel-button {
        display: none;
    }
`;

SearchInput.defaultProps = {
    flex: '1 0 auto',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    fontWeight: 'inherit',
    padding: 'sm',
    borderStyle: 'none',
    bgColor: 'inherit',
    outline: 'none',
};

const SearchLabel = styled<any, any>('label', 'searchLabel', 'search')`
    ${propStyles};
`;

SearchLabel.defaultProps = {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    fontFamily: 'heading',
    fontWeight: 'medium',
    color: 'body',
};

interface SearchProps {
    theme?: Theme;
    translationStore?: TranslationStore;
    accessibilityStore?: AccessibilityStore;
    accessibilityStoreKey?: AccessibilityStoreSearchKey;
    loadingStore?: LoadingStore;
    total: number;
    search: IdentifierSearch;
    searchInputId: string;
    metricIds: {
        search: string;
        closeIcon: string;
    };
    hasFocus?: boolean;
    scrollIntoView?: boolean;
    onChange: (e) => void;
    clearSearch: () => void;
}

@inject('translationStore', 'accessibilityStore', 'loadingStore')
@observer
class Search extends Component<SearchProps> {
    searchInput: HTMLInputElement;
    searchLabelId = 'fund-center-search-label';

    @computed
    get searchLabelText(): string | undefined {
        const {
            accessibilityStore,
            accessibilityStoreKey = 'search',
            translationStore,
        } = this.props;

        const searchLabelText = accessibilityStore.getDocumentSearchLabel(
            accessibilityStoreKey
        );

        if (searchLabelText) {
            return translate(translationStore, searchLabelText);
        }

        return undefined;
    }

    @computed
    get showSearchLabel(): boolean {
        const {
            accessibilityStore,
            accessibilityStoreKey = 'search',
        } = this.props;

        return accessibilityStore.showSearchLabel(accessibilityStoreKey);
    }

    @computed
    get searchLabel(): React.ReactNode {
        const { theme, searchInputId = SEARCH_INPUT_ID } = this.props;

        const searchLabelComponent = (
            <SearchLabel
                id={this.searchLabelId}
                htmlFor={searchInputId}
                {...theme.SearchLabel}
            >
                {this.searchLabelText || this.searchPlaceholder}
            </SearchLabel>
        );

        if (this.showSearchLabel) {
            return searchLabelComponent;
        } else {
            return (
                <VisuallyHiddenWrapper>
                    {searchLabelComponent}
                </VisuallyHiddenWrapper>
            );
        }
    }

    @computed
    get searchPlaceholder(): string {
        const { translationStore, total, search } = this.props;
        return translate(
            translationStore,
            total === 1
                ? search.placeholder.format.singular
                : search.placeholder.format.plural,
            {
                total,
                properties: search.properties.join(', '),
            }
        );
    }

    // Store reference to the input element
    setSearchInput = (input: HTMLInputElement) => {
        this.searchInput = input;
    };

    // Focus the search input element
    focusSearchInput = () => {
        if (this.searchInput) {
            const { scrollIntoView = true } = this.props;
            if (scrollIntoView === true) {
                this.searchInput.scrollIntoView(true);
            }
            this.searchInput.focus();
        }
    };

    // Clear search and return focus to search input
    clearSearch = () => {
        const { clearSearch } = this.props;
        if (clearSearch) {
            clearSearch();
        }
        this.focusSearchInput();
    };

    // When the Search component is used within a dialog, popup, overlay etc we may want to close the parent component when the ESCAPE key is pressed.
    // This is in conflict with the default behaviour for input type search where the ESCAPE key clears the input.
    // To accommodate this we must prevent the keyDown event from bubbling when the ESCAPE key is pressed and the search input has a value.
    onKeyDown = event => {
        const { search } = this.props;
        if (event.key === KEYBOARD_KEYS.ESCAPE && search.value !== '') {
            event.stopPropagation();
        }
    };

    // Optionally focus the search input
    componentDidMount() {
        const { hasFocus = false } = this.props;
        if (hasFocus) {
            // Focus happens to quickly and causes the screen to scroll away from where the dropdown is going to appear.
            setTimeout(() => {
                this.focusSearchInput();
            }, 250);
        }
    }

    render() {
        const {
            theme,
            translationStore,
            total,
            onChange,
            search,
            searchInputId = SEARCH_INPUT_ID,
            metricIds = {
                search: 'entitySearch',
                closeIcon: 'entitySearch-close',
            },
            loadingStore,
        } = this.props;
        return (
            <SearchWrapper {...theme.SearchWrapper}>
                {this.searchLabel}
                <Icon
                    fileName="SearchIcon"
                    height="1.6em"
                    width="1.6em"
                    {...theme.SearchIcon}
                />
                {search.placeholder.badge && search.placeholder.badge.show && (
                    <Badge
                        marginX="sm"
                        {...(search.placeholder.badge.align !== 'left' &&
                            Badge.rightAlignStyleProps)}
                        {...theme.SearchBadge}
                        aria-live="polite"
                        role="status"
                        aria-describedby={this.searchLabelId}
                    >
                        {translate(
                            translationStore,
                            total === 1
                                ? search.placeholder.badge.format.singular
                                : search.placeholder.badge.format.plural,
                            {
                                total,
                                properties: search.properties.join(', '),
                            }
                        )}
                    </Badge>
                )}
                <SearchInput
                    id={searchInputId}
                    type="search"
                    value={search.value}
                    onChange={e => onChange(e.target.value)}
                    onKeyDown={this.onKeyDown}
                    placeholder={this.searchPlaceholder}
                    autoCapitalize="off"
                    autoComplete="off"
                    spellCheck="false"
                    autoCorrect="off"
                    data-metric-id={metricIds.search}
                    ref={this.setSearchInput}
                    aria-busy={loadingStore.global}
                    aria-relevant="all"
                    {...theme.SearchInput}
                />
                {search.value && (
                    <SearchClearButton
                        onClick={this.clearSearch}
                        aria-label={translate(translationStore, 'Clear Search')}
                        data-metric-id={metricIds.closeIcon}
                        {...theme.SearchClearButton}
                    >
                        <Icon
                            fileName="closeIcon"
                            cursor="pointer"
                            height="1.6em"
                            width="1.6em"
                            disableAltText={true}
                            {...theme.SearchCloseIcon}
                            {...Icon.rightAlignStyleProps}
                        />
                    </SearchClearButton>
                )}
            </SearchWrapper>
        );
    }
}

export default withTheme(Search);



// WEBPACK FOOTER //
// ./src/Components/Search.tsx